import React, { useEffect, useState } from 'react';

/**
 * Unified modal for AI credit / feature limit errors.
 *
 * Listens for the `ai-limit-error` CustomEvent dispatched by
 * `utils/aiErrorHandler.js#handleAIError`. Renders a single, consistent
 * modal everywhere an AI endpoint returns 429 CREDITS_EXCEEDED or
 * 403 FEATURE_NOT_AVAILABLE.
 *
 * Mounted once at the App root — no props required.
 */

// Mirrors auth.AI_TIER_LIMITS. Trial grants Max-level credits + full Digital Twin (#43).
// Trial raised 45 -> 90 (2026-08-02) so the 80-credit full meal plan is actually reachable;
// keep this in lockstep with auth.AI_TIER_LIMITS — this table is what the user reads at the
// exact moment they hit the ceiling, so a stale number here is a lie at the worst moment.
const TIER_DETAILS = {
  trial:  { label: 'Trial', credits: 90, twin: 'Full (trial)' },
  base:   { label: 'Base',  credits: 15, twin: 'None' },
  pro:    { label: 'Pro',   credits: 45, twin: 'Basic' },
  max:    { label: 'Max',   credits: 90, twin: 'Full' },
};

// The purchasable plans shown in the upgrade comparison (trial is not purchasable).
const PAID_TIERS = ['base', 'pro', 'max'];

function AILimitModal({ onUpgradeClick }) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const handler = (event) => {
      setDetail(event.detail || null);
    };
    window.addEventListener('ai-limit-error', handler);
    return () => window.removeEventListener('ai-limit-error', handler);
  }, []);

  if (!detail) return null;

  const isBlocked = detail.error === 'FEATURE_NOT_AVAILABLE';
  const isCredits = detail.error === 'CREDITS_EXCEEDED';
  const tier = (detail.tier || 'trial').toLowerCase();
  const currentTier = TIER_DETAILS[tier] || TIER_DETAILS.trial;

  const title = isBlocked ? 'Feature Not Available On Your Plan' : 'Daily AI Credits Reached';

  const close = () => setDetail(null);

  const handleUpgrade = () => {
    close();
    if (typeof onUpgradeClick === 'function') onUpgradeClick();
  };

  return (
    <div className="modal-overlay ailm-overlay" onClick={close}>
      <div className="modal-content ailm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close ailm-close" onClick={close} aria-label="Close">×</button>
        </div>

        <div className="modal-body ailm-body">
          <p className="ailm-message">{detail.message}</p>

          {isCredits && typeof detail.credits_remaining === 'number' && (
            <div className="ailm-usage">
              <div className="ailm-usage-row">
                <span className="ailm-usage-label">This action costs</span>
                <span className="ailm-usage-value">{detail.credit_cost} credit{detail.credit_cost !== 1 ? 's' : ''}</span>
              </div>
              <div className="ailm-usage-row">
                <span className="ailm-usage-label">Credits remaining today</span>
                <span className="ailm-usage-value">{detail.credits_remaining} / {detail.daily_limit}</span>
              </div>
              <div className="ailm-usage-row">
                <span className="ailm-usage-label">Your plan</span>
                <span className="ailm-usage-value">{currentTier.label}</span>
              </div>
            </div>
          )}

          {isBlocked && (
            <div className="ailm-usage">
              <div className="ailm-usage-row">
                <span className="ailm-usage-label">Your plan</span>
                <span className="ailm-usage-value">{currentTier.label}</span>
              </div>
            </div>
          )}

          <div className="ailm-tiers">
            <h4 className="ailm-tiers-title">Available Plans</h4>
            <table className="ailm-tiers-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Daily Credits</th>
                  <th>Digital Twin</th>
                </tr>
              </thead>
              <tbody>
                {PAID_TIERS.map((key) => {
                  const info = TIER_DETAILS[key];
                  return (
                    <tr key={key} className={key === tier ? 'ailm-tier-current' : ''}>
                      <td>{info.label}{key === tier ? ' (current)' : ''}</td>
                      <td>{info.credits}</td>
                      <td>{info.twin}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {isCredits && (
            <p className="ailm-reset-note">
              Credits reset at midnight in your local timezone.
            </p>
          )}
        </div>

        <div className="modal-footer ailm-footer">
          <button className="btn btn-secondary" onClick={close}>Dismiss</button>
          {(() => {
            const canBuyCredits = isCredits && PAID_TIERS.includes(tier);  // paid subs (incl. Max) can top up
            const canUpgrade = tier !== 'max';
            if (!canBuyCredits && !canUpgrade) return null;
            let label;
            if (isBlocked) label = 'Upgrade My Plan';
            else if (canBuyCredits && canUpgrade) label = 'Buy Credits or Upgrade';
            else if (canBuyCredits) label = 'Buy Credits';
            else label = 'View Plans & Upgrade';
            return <button className="btn btn-primary" onClick={handleUpgrade}>{label}</button>;
          })()}
        </div>
      </div>
    </div>
  );
}

export default AILimitModal;
